import { useEffect, useState } from "react";
import { marked } from "marked";
import { Card, Heading, Stack } from "../ui/primitives.js";

/**
 * In-app themed renderer for the public markdown docs (instructor guide,
 * syllabus mapping, scenario rationale, privacy, terms). The .md files
 * stay in `public/` so search engines can crawl them as plain pages, but
 * everywhere we link from inside the app we route to this component so
 * the page shares the site theme.
 */

export type DocKey =
  | "instructor-guide"
  | "syllabus-mapping"
  | "scenario-rationale"
  | "privacy"
  | "terms"
  | "store-links";

const DOC_TITLES: Record<DocKey, string> = {
  "instructor-guide": "Instructor guide",
  "syllabus-mapping": "RYA G34 syllabus mapping",
  "scenario-rationale": "Scenario rationale",
  privacy: "Privacy",
  terms: "Terms",
  "store-links": "Get the tablet app",
};

export function DocPage(props: { docKey: DocKey }): JSX.Element {
  const [html, setHtml] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setHtml(null);
    setError(null);
    fetch(`/${props.docKey}.md`, { cache: "no-cache" })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.text();
      })
      .then(async (md) => {
        const rendered = await marked.parse(md, { gfm: true });
        if (!cancelled) setHtml(rendered);
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : String(e));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [props.docKey]);

  return (
    <div
      style={{
        width: "100%",
        maxWidth: 880,
        margin: "0 auto",
        padding: "16px 24px 64px",
      }}
    >
      <Card padding={28}>
        <Stack gap={20}>
          <Heading level={1} style={{ fontSize: 30 }}>
            {DOC_TITLES[props.docKey]}
          </Heading>
          {error !== null && (
            <p style={{ color: "var(--danger)", margin: 0 }}>
              Failed to load document: {error}
            </p>
          )}
          {html === null && error === null && (
            <p style={{ color: "var(--text-muted)", margin: 0 }}>Loading…</p>
          )}
          {html !== null && (
            <div className="doc-content" dangerouslySetInnerHTML={{ __html: html }} />
          )}
        </Stack>
      </Card>
    </div>
  );
}
